/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState, useContext } from 'react'
import { Pagination, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from 'react-foundation'
import { useLocation } from '@reach/router'
import UserContext from '../../../UserContext'
import { SaveEvent, PageChangeEvent } from '../AnalyticsEvents'

const isPageDisplayed = (currentPage: number, lastPage: number) => (testPage: number) => {
    if ([1, lastPage].includes(testPage)) return true
    if (currentPage <= 3) return testPage <= 6
    if (currentPage + 3 >= lastPage) return testPage >= lastPage - 6

    return Math.abs(currentPage - testPage) <= 3
}

const isEllipsisPage = (currentPage: number, lastPage: number) => (testPage: number) => {
    if ([1, lastPage].includes(testPage)) return false
    if (currentPage <= 3) return testPage > 5
    if (currentPage + 3 >= lastPage) return lastPage - testPage === 6

    return Math.abs(currentPage - testPage) === 3
}

type PaginatorProps = {
    defaultPageSize?: number
    onChange: (page: number, pageSize: number) => void
    total: number
    currentPage?: number
}

const Paginator: React.FC<PaginatorProps> = (props) => {
    const [page, setPage] = useState<number>(props.currentPage ?? 1)
    const [pageSize, setPageSize] = useState<number>(props.defaultPageSize ?? 10)
    const pageCount = useMemo(() => Math.ceil(props.total / pageSize), [pageSize, props.total])
    const { user } = useContext(UserContext)
    const { pathname } = useLocation()

    useEffect(() => { if (props.currentPage) setPage(props.currentPage) }, [props.currentPage])
    useEffect(() => { if (props.defaultPageSize) setPageSize(props.defaultPageSize) }, [props.defaultPageSize])

    const handlePageSelect = (type: string, p: number) => () => {
        setPage(p)
        SaveEvent(PageChangeEvent(user, pathname, type, p))
        props.onChange(p, pageSize)
    }

    const handlePageSizeSelect = (s: number) => {
        setPageSize(s)
        setPage(1)
        SaveEvent(PageChangeEvent(user, pathname, 'size', s))
        props.onChange(1, s)
    }

    return (
        <div className='paginator'>
            <select className='paginator-page-size' onChange={e => handlePageSizeSelect(parseInt(e.target.value))} value={pageSize}>
                {[10, 25, 50, 100].map((o, i) => <option key={i} value={o}>{o} / page</option>)}
            </select>

            <Pagination className='paginator-page-select'>
                <PaginationPrevious isDisabled={page === 1}>
                    {page === 1 ? <span>Previous</span> : <a onClick={handlePageSelect('prev', page - 1)}>Previous</a>}
                </PaginationPrevious>
                {
                    new Array(pageCount)
                        .fill(0)
                        .map((_, i) => i + 1)
                        .filter(isPageDisplayed(page, pageCount))
                        .map(i => isEllipsisPage(page, pageCount)(i)
                            ? <PaginationEllipsis key={i} />
                            : <PaginationItem key={i} isCurrent={i === page}>{i === page
                                ? <span>{i}</span>
                                : <a onClick={handlePageSelect('page', i)}>{i}</a>}</PaginationItem>
                        )
                }
                <PaginationNext isDisabled={page === pageCount}>
                    {page === pageCount ? <span>Next</span> : <a onClick={handlePageSelect('next', page + 1)}>Next</a>}
                </PaginationNext>
            </Pagination>
        </div>
    )
}

export default Paginator
